import type { FC } from 'react';
import React, { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { PayoutColumn, PayoutTabValues } from './types';
import type { PayoutSortColumn } from './types';

import type { LabelColor, UrlSort } from '@pflegenavi/web-components';
import {
  type ConfigType,
  Label,
  makeConfig,
  PaginatedTable2,
  Typography,
} from '@pflegenavi/web-components';
import { Stack } from '@mui/material';
import { YEAR_SHORTMONTH_DAY_FORMAT } from '@pflegenavi/shared/constants';
import {
  useFormatDate,
  useFormatting,
} from '@pflegenavi/frontend/localization';
import { IconTooltip } from '@pflegenavi/transaction-components';
import { useTheme } from '@mui/material/styles';
import type { GetPayoutsResult } from '@pflegenavi/service-provider-frontend/api';
import { PayoutReconciliationDialog } from './PayoutReconciliationDialog';

interface PayoutsTableProps {
  isLoading: boolean;
  isError: boolean;
  payouts: GetPayoutsResult | undefined;
  totalCount: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  tab: PayoutTabValues;
  onTabChange: (tab: PayoutTabValues) => void;
  sort: UrlSort<PayoutSortColumn>;
  onSortChange: (sort: UrlSort<PayoutSortColumn>) => void;
}

interface PayoutsTableRow {
  id: string;
  initiationDate: Date;
  ibanLast4: string;
  amount: number;
  statusColor: LabelColor;
  statusString: string;
}

type ColumnNames =
  | PayoutColumn.ibanLast4
  | PayoutColumn.initiationDate
  | PayoutColumn.amount
  | PayoutColumn.status
  | PayoutColumn.reconciliation;

type Filters = '';

export const PayoutsTable: FC<PayoutsTableProps> = ({
  isLoading,
  isError,
  payouts,
  totalCount,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  tab,
  onTabChange,
  sort,
  onSortChange,
}) => {
  const { t } = useTranslation();
  const fDate = useFormatDate();
  const { fCurrencyCents } = useFormatting();

  const rows = useMemo<PayoutsTableRow[]>(
    () =>
      payouts?.data?.map((payout) => {
        const statusColor =
          payout.state === 'CREATED'
            ? 'warning'
            : payout.state === 'SUCCEEDED'
            ? 'success'
            : ('error' as LabelColor);
        const statusString =
          payout.state === 'CREATED'
            ? 'pending'
            : payout.state === 'SUCCEEDED'
            ? 'completed'
            : 'failed';

        return {
          id: payout.id,
          initiationDate: new Date(payout.inserted_at),
          ibanLast4: payout.iban_last_4,
          amount: payout.amount,
          statusColor,
          statusString,
        };
      }) ?? [],
    [payouts]
  );

  const config: ConfigType<PayoutsTableRow, ColumnNames, Filters> = useMemo(
    () =>
      makeConfig<PayoutsTableRow, ColumnNames, Filters>({
        showAll: undefined,
        onClickRow: undefined,
        hoverRow: true,
        tabs: {
          selectedItem: tab,
          updateFilterModel: onTabChange,
          items: [
            {
              value: PayoutTabValues.All,
              label: t('accounting.payouts.states.all'),
              color: 'default',
              count: undefined,
            },
            {
              value: PayoutTabValues.Paid,
              label: t('accounting.payouts.states.completed'),
              color: 'success',
              count: undefined,
            },
            {
              value: PayoutTabValues.Pending,
              label: t('accounting.payouts.states.pending'),
              color: 'warning',
              count: undefined,
            },
            {
              value: PayoutTabValues.Failed,
              label: t('accounting.payouts.states.failed'),
              color: 'error',
              count: undefined,
            },
          ],
        },
        columns: [
          {
            key: PayoutColumn.ibanLast4,
            label: t('accounting.payouts.table-head.4-digits'),
            align: 'left',
            component: ({ row: { ibanLast4 } }) => (
              <Typography variant="subtitle2" noWrap>
                **** {ibanLast4}
              </Typography>
            ),
          },
          {
            key: PayoutColumn.initiationDate,
            label: t('accounting.payouts.table-head.initiation-date'),
            align: 'left',
            onSort: (direction) =>
              onSortChange({
                column: 'inserted_at',
                direction: direction === 'ASC' ? 'asc' : 'desc',
              }),
            component: ({ row: { initiationDate } }) => (
              <Typography variant="subtitle2" noWrap>
                {fDate(new Date(initiationDate), YEAR_SHORTMONTH_DAY_FORMAT)}
              </Typography>
            ),
          },
          {
            key: PayoutColumn.amount,
            label: t('accounting.payouts.table-head.amount'),
            align: 'right',
            minWidth: 100,
            maxWidth: 150,
            sx: { paddingRight: 8 },
            component: ({ row: { amount } }) => (
              <Stack sx={{ paddingRight: 6 }}>
                <Typography variant="subtitle2">
                  {fCurrencyCents(amount)}
                </Typography>
              </Stack>
            ),
          },
          {
            key: PayoutColumn.status,
            label: t('accounting.payouts.table-head.status'),
            align: 'left',
            component: ({ row: { statusColor, statusString } }) => (
              <Label color={statusColor} variant="ghost">
                {t(`accounting.payouts.states.${statusString}`)}
              </Label>
            ),
          },
          {
            key: PayoutColumn.reconciliation,
            label: t('accounting.payouts.table-head.reconciliation'),
            align: 'left',
            component: (params: { row: PayoutsTableRow }) => (
              <PayoutReconciliationDownload
                payoutId={params.row.id}
                initiationDate={params.row.initiationDate}
              />
            ),
          },
        ],
        sortColumn: {
          column: PayoutColumn.initiationDate,
          direction: sort.direction === 'asc' ? 'ASC' : 'DESC',
        },
        emptyRowHeight: 77,
        pagination: {
          total: totalCount,
          page,
          onPageChange,
          pageSize,
          onPageSizeChange,
          pageSizeOptions: [5, 10, 25, 50],
        },
      }),
    [
      t,
      tab,
      onTabChange,
      sort,
      onSortChange,
      totalCount,
      page,
      onPageChange,
      pageSize,
      onPageSizeChange,
      fDate,
      fCurrencyCents,
    ]
  );

  return (
    <PaginatedTable2
      name="AccountingPayoutsTable"
      isLoading={isLoading}
      isError={isError}
      config={config}
      rows={rows}
    />
  );
};

interface PayoutReconciliationDownloadProps {
  payoutId: string;
  initiationDate: Date;
}

const PayoutReconciliationDownload: FC<PayoutReconciliationDownloadProps> = ({
  payoutId,
  initiationDate,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <>
      <IconTooltip
        title={t('tooltip.download-reconciliation')}
        color={theme.palette.success.main}
        icon="check"
        onClick={() => setDialogOpen(true)}
      />
      <PayoutReconciliationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        payoutId={payoutId}
        initiationDate={initiationDate}
      />
    </>
  );
};
